import React from 'react';
import { Edit, Trash2, FileText, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

const GastoTable = ({
  gastos,
  categorias,
  proveedores,
  mediosPago,
  ordenColumna,
  ordenDireccion,
  onOrdenar,
  onEditar,
  onEliminar,
  onMarcarPagado,
  onVerComprobante,
  onProveedorClick
}) => {
  const formatCurrency = (value) => {
    return `$${Math.round(value || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const obtenerNombreCategoria = (categoriaId) => {
    const cat = categorias.find(c => c.id_categoria === categoriaId);
    return cat?.nombre || 'Sin categoría';
  };

  const obtenerNombreMedioPago = (medioPagoId) => {
    if (!medioPagoId) return '-';
    const medio = mediosPago.find(m => m.id_medio_pago === medioPagoId);
    return medio?.nombre || '-';
  };

  const renderOrdenIcon = (columna) => {
    if (ordenColumna !== columna) {
      return <ArrowUpDown className="w-4 h-4 ml-1 text-[#0E3A53]/30" />;
    }
    return ordenDireccion === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1 text-[#1AA1D8]" />
      : <ArrowDown className="w-4 h-4 ml-1 text-[#1AA1D8]" />;
  };

  if (gastos.length === 0) {
    return (
      <div className="text-center py-12 text-[#0E3A53]/60">
        <p className="text-lg">No hay gastos que mostrar</p>
        <p className="text-sm mt-2">Crea tu primer gasto con el botón "Nuevo Gasto"</p>
      </div>
    );
  }

  return (
    <div>
      {/* Vista Móvil: Tarjetas táctiles individuales */}
      <div className="block md:hidden space-y-3" data-testid="gastos-mobile-list">
        {gastos.map((gasto) => {
          const esRecurrente = gasto.gasto_recurrente_id;
          const esPendiente = gasto.estado === 'PENDIENTE';
          const nombreCategoria = obtenerNombreCategoria(gasto.categoria_id);
          const nombreMedioPago = obtenerNombreMedioPago(gasto.medio_pago_id);

          return (
            <div
              key={gasto.id_gasto}
              className={`p-3.5 rounded-xl border transition-all ${
                esPendiente
                  ? 'bg-amber-50/60 border-amber-200 shadow-sm'
                  : 'bg-white border-[#8FE0FF]/40 shadow-sm hover:border-[#1AA1D8]/40'
              }`}
              data-testid={`gasto-card-${gasto.id_gasto}`}
            >
              {/* Cabecera de la tarjeta: Categoría + Fecha */}
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-[#1AA1D8]/10 text-[#0E3A53] border border-[#1AA1D8]/20 truncate max-w-[180px]">
                  {nombreCategoria}
                </span>
                <div className="flex items-center gap-1.5 text-xs text-[#0E3A53]/70 font-medium shrink-0">
                  {esRecurrente && (
                    <RefreshCw className="w-3 h-3 text-[#1AA1D8]" title="Gasto recurrente" />
                  )}
                  <span>{formatDate(gasto.fecha)}</span>
                </div>
              </div>

              {/* Fila central: Proveedor + Monto */}
              <div className="flex items-baseline justify-between gap-2 my-1.5">
                <div className="min-w-0 flex-1">
                  {gasto.proveedor_id ? (
                    <button
                      onClick={() => onProveedorClick && onProveedorClick(gasto.proveedor_id, gasto.proveedor)}
                      className="text-sm font-bold text-[#1AA1D8] hover:underline text-left truncate block max-w-full"
                      title="Ver análisis del proveedor"
                    >
                      {gasto.proveedor}
                    </button>
                  ) : (
                    <p className="text-sm font-bold text-[#0E3A53] truncate">
                      {gasto.proveedor || 'Sin proveedor'}
                    </p>
                  )}
                  {nombreMedioPago !== '-' && (
                    <span className="text-[11px] text-[#0E3A53]/60 block mt-0.5">
                      💳 {nombreMedioPago}
                    </span>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <span className="text-base sm:text-lg font-bold text-[#FF7F6A]">
                    {formatCurrency(gasto.monto)}
                  </span>
                </div>
              </div>

              {/* Fila inferior: Estado interactivo + Acciones */}
              <div className="flex items-center justify-between pt-2.5 mt-2 border-t border-[#0E3A53]/10">
                {esPendiente ? (
                  <button
                    onClick={() => onMarcarPagado(gasto)}
                    className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
                  >
                    ⏳ Pendiente
                  </button>
                ) : (
                  <button
                    onClick={() => onMarcarPagado(gasto)}
                    className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
                  >
                    ✓ Pagado
                  </button>
                )}

                <div className="flex items-center gap-1">
                  {gasto.comprobante_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onVerComprobante(gasto)}
                      className="h-8 w-8 p-0 text-[#1AA1D8] hover:bg-[#8FE0FF]/20"
                      title="Ver comprobante"
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditar(gasto)}
                    className="h-8 w-8 p-0 text-[#1AA1D8] hover:bg-[#8FE0FF]/20"
                    title="Editar"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEliminar(gasto)}
                    className="h-8 w-8 p-0 text-[#FF7F6A] hover:bg-[#FF7F6A]/10"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Vista Desktop: Tabla tradicional */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full" data-testid="gastos-table">
          <thead>
            <tr className="border-b-2 border-[#FF7F6A]/30">
              <th 
                className="px-4 py-3 text-left text-sm font-semibold text-[#0E3A53] cursor-pointer hover:bg-[#8FE0FF]/10"
                onClick={() => onOrdenar('fecha')}
              >
                <div className="flex items-center">
                  Fecha
                  {renderOrdenIcon('fecha')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-sm font-semibold text-[#0E3A53] cursor-pointer hover:bg-[#8FE0FF]/10"
                onClick={() => onOrdenar('categoria')}
              >
                <div className="flex items-center">
                  Categoría
                  {renderOrdenIcon('categoria')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-sm font-semibold text-[#0E3A53] cursor-pointer hover:bg-[#8FE0FF]/10"
                onClick={() => onOrdenar('proveedor')}
              >
                <div className="flex items-center">
                  Proveedor
                  {renderOrdenIcon('proveedor')}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#0E3A53]">
                Medio de Pago
              </th>
              <th 
                className="px-4 py-3 text-right text-sm font-semibold text-[#0E3A53] cursor-pointer hover:bg-[#8FE0FF]/10"
                onClick={() => onOrdenar('monto')}
              >
                <div className="flex items-center justify-end">
                  Monto
                  {renderOrdenIcon('monto')}
                </div>
              </th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-[#0E3A53]">Estado</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-[#0E3A53]">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {gastos.map((gasto) => {
              const esRecurrente = gasto.gasto_recurrente_id;
              const esPendiente = gasto.estado === 'PENDIENTE';
              
              return (
                <tr 
                  key={gasto.id_gasto} 
                  className={`border-b border-[#FF7F6A]/10 hover:bg-[#F7E8C9]/20 transition-colors ${esPendiente ? 'bg-amber-50/50' : ''}`}
                  data-testid={`gasto-row-${gasto.id_gasto}`}
                >
                  <td className="px-4 py-3 text-sm text-[#0E3A53]">
                    <div className="flex items-center gap-2">
                      {esRecurrente && (
                        <RefreshCw className="w-3 h-3 text-[#1AA1D8]" title="Gasto recurrente" />
                      )}
                      {formatDate(gasto.fecha)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#0E3A53]">
                    {obtenerNombreCategoria(gasto.categoria_id)}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-[#0E3A53]">
                    {gasto.proveedor_id ? (
                      <button 
                        onClick={() => onProveedorClick && onProveedorClick(gasto.proveedor_id, gasto.proveedor)}
                        className="text-[#1AA1D8] hover:underline hover:text-[#1590c2] text-left"
                        title="Ver análisis anual del proveedor"
                      >
                        {gasto.proveedor}
                      </button>
                    ) : (
                      <span className="text-gray-500">{gasto.proveedor || 'Sin proveedor'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#0E3A53]">
                    {obtenerNombreMedioPago(gasto.medio_pago_id)}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-[#FF7F6A] text-right">
                    {formatCurrency(gasto.monto)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {esPendiente ? (
                      <button
                        onClick={() => onMarcarPagado(gasto)}
                        className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors cursor-pointer"
                        title="Clic para marcar como pagado"
                      >
                        Pendiente
                      </button>
                    ) : (
                      <button
                        onClick={() => onMarcarPagado(gasto)}
                        className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors cursor-pointer"
                        title="Clic para marcar como pendiente"
                      >
                        Pagado
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      {gasto.comprobante_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onVerComprobante(gasto)}
                          className="text-[#1AA1D8] hover:bg-[#8FE0FF]/20"
                          title="Ver comprobante"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditar(gasto)}
                        className="text-[#1AA1D8] hover:bg-[#8FE0FF]/20"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEliminar(gasto)}
                        className="text-[#FF7F6A] hover:bg-[#FF7F6A]/10"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GastoTable;
